import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { useOrg } from '../context/OrgContext';
import { createCustomer, checkEmailUnique } from '../services/customer.service';
import { addCustomerCrop } from '../services/customerCrop.service';
import { getAllCropTypes } from '../services/cropType.service';
import { getAllRegions } from '../services/region.service';
import { toastSuccess } from '../components/core';
import { validateCustomerForm, type CustomerFormFields, type CustomerFormErrors } from '../utils/validation';
import { Navbar } from '../components/Navbar';
import { BackButton, Button, Card, Input, Spinner, CustomerCropsTable, type CropRow } from '../components/core';

export default function AddCustomerPage() {
  const { user, signOut } = useAuth();
  const { org, orgId, orgSlug } = useOrg();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [fields, setFields] = useState<CustomerFormFields>({
    firstName: '',
    lastName: '',
    phone: '',
    address: '',
    email: '',
    registrationDate: '',
  });
  const [errors, setErrors] = useState<CustomerFormErrors>({});
  const [saving, setSaving] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const [cropRows, setCropRows] = useState<CropRow[]>([]);
  const [cropError, setCropError] = useState<string | undefined>(undefined);
  const [cropTypes, setCropTypes] = useState<{ id: string; name: string }[]>([]);
  const [regions, setRegions] = useState<{ id: string; name: string }[]>([]);

  useEffect(() => {
    if (!orgId) return;
    Promise.all([getAllCropTypes(orgId), getAllRegions(orgId)])
      .then(([cts, regs]) => {
        setCropTypes(cts);
        setRegions(regs);
      })
      .catch((err) => {
        console.error('[AddCustomerPage] load reference data error:', err);
        // options failed to load — table will show empty selects
      });
  }, [orgId]);

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    const { name, value } = e.target;
    setFields(prev => ({ ...prev, [name]: value }));
    if (errors[name as keyof CustomerFormErrors]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  }

  function handleCropChange(rows: CropRow[]) {
    setCropRows(rows);
    if (cropError) setCropError(undefined);
  }

  function validateCrops(rows: CropRow[]): boolean {
    const completeRows = rows.filter(r => r.cropTypeId && r.regionId);
    if (completeRows.length === 0) {
      setCropError(t('validation.atLeastOneCrop'));
      return false;
    }
    const keys = completeRows.map(r => `${r.cropTypeId}_${r.regionId}`);
    const hasDups = keys.length !== new Set(keys).size;
    if (hasDups) {
      // Inline row errors are shown by the component; block submit
      setCropError(t('validation.duplicateCropRow'));
      return false;
    }
    return true;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();

    const fieldErrors = validateCustomerForm(fields, t);
    const cropsValid = validateCrops(cropRows);

    if (Object.keys(fieldErrors).length > 0) {
      setErrors(fieldErrors);
    }
    if (!cropsValid || Object.keys(fieldErrors).length > 0) {
      return;
    }

    if (!user || !orgId) return;
    setSaving(true);
    setSubmitError(null);

    try {
      if (fields.email.trim()) {
        const isUnique = await checkEmailUnique(orgId, fields.email.trim());
        if (!isUnique) {
          setErrors(prev => ({ ...prev, email: t('validation.emailTaken') }));
          return;
        }
      }

      const customer = await createCustomer(orgId, user.uid, {
        firstName: fields.firstName.trim(),
        lastName: fields.lastName.trim(),
        phone: fields.phone.trim(),
        address: fields.address.trim(),
        ...(fields.email.trim() ? { email: fields.email.trim() } : {}),
        ...(fields.registrationDate ? { registrationDate: fields.registrationDate } : {}),
      });

      const completeRows = cropRows.filter(r => r.cropTypeId && r.regionId);
      await Promise.all(completeRows.map(row => addCustomerCrop(orgId, customer.id, row)));

      toastSuccess(t('toasts.customerCreated'));
      navigate(`/org/${orgSlug}/customers`);
    } catch (err: unknown) {
      console.error('[AddCustomerPage] save error:', err);
      const message = err && typeof err === 'object' && 'message' in err
        ? String((err as { message: unknown }).message)
        : t('form.saveError');
      setSubmitError(message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="min-h-screen bg-green-50">
      <Navbar displayName={user?.displayName ?? null} onSignOut={signOut} onLogoClick={() => navigate('/')} onOrgNameClick={() => navigate(`/org/${orgSlug}`)} onAvatarClick={() => navigate("/profile")} orgName={org.name} />

      <main className="mx-auto max-w-2xl px-4 py-6 sm:px-6">
        <BackButton />

        {/* Page heading */}
        <div className="mb-5">
          <h1 className="font-display text-xl font-semibold text-gray-900">{t('form.addTitle')}</h1>
          <p className="mt-1 font-body text-sm text-gray-500">
            {t('form.addSubtitle')}
          </p>
        </div>

        <form onSubmit={handleSubmit} noValidate>
          <Card variant="form">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Input
                name="firstName"
                label={t('form.firstName')}
                required
                placeholder={t('form.firstNamePlaceholder')}
                value={fields.firstName}
                onChange={handleChange}
                error={errors.firstName}
                autoComplete="given-name"
              />
              <Input
                name="lastName"
                label={t('form.lastName')}
                required
                placeholder={t('form.lastNamePlaceholder')}
                value={fields.lastName}
                onChange={handleChange}
                error={errors.lastName}
                autoComplete="family-name"
              />
              <Input
                name="phone"
                label={t('form.phone')}
                required
                type="tel"
                placeholder={t('form.phonePlaceholder')}
                value={fields.phone}
                onChange={handleChange}
                error={errors.phone}
                autoComplete="tel"
              />
              <Input
                name="address"
                label={t('form.address')}
                required
                placeholder={t('form.addressPlaceholder')}
                value={fields.address}
                onChange={handleChange}
                error={errors.address}
                autoComplete="street-address"
              />
              <Input
                name="email"
                label={t('form.email')}
                type="email"
                placeholder={t('form.emailPlaceholder')}
                value={fields.email}
                onChange={handleChange}
                error={errors.email}
                autoComplete="email"
              />
              <Input
                name="registrationDate"
                label={t('form.registrationDate')}
                type="date"
                value={fields.registrationDate}
                onChange={handleChange}
              />
            </div>

            <div className="mt-4">
              <CustomerCropsTable
                value={cropRows}
                cropTypes={cropTypes}
                regions={regions}
                onChange={handleCropChange}
                error={cropError}
                disabled={saving}
              />
            </div>
          </Card>

          {submitError && (
            <div className="mt-4 rounded bg-red-50 px-4 py-3 font-body text-sm text-red-600">
              {submitError}
            </div>
          )}

          <div className="mt-4 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <Button
              type="button"
              variant="secondary"
              onClick={() => navigate('/customers')}
              disabled={saving}
            >
              {t('form.cancel')}
            </Button>
            <Button type="submit" variant="primary" disabled={saving}>
              {saving ? (
                <>
                  <Spinner size="sm" />
                  {t('form.saving')}
                </>
              ) : (
                t('form.save')
              )}
            </Button>
          </div>
        </form>
      </main>
    </div>
  );
}
